import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from './api';
import type { PublicEvent } from './types';

interface EventContextValue {
  event: PublicEvent | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

const EventContext = createContext<EventContextValue | null>(null);

/**
 * Loads the public event once and shares it across the site. Registration
 * limits, categories and the open/closed state all come from here so the form
 * always matches what the API will accept.
 */
export function EventProvider({ children }: { children: ReactNode }) {
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    api
      .getEvent(controller.signal)
      .then((loaded) => {
        setEvent(loaded);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(
          err instanceof ApiError ? err.message : 'Could not load the event details right now.',
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [reloadToken]);

  return (
    <EventContext.Provider value={{ event, loading, error, reload }}>
      {children}
    </EventContext.Provider>
  );
}

export function useEventContext(): EventContextValue {
  const context = useContext(EventContext);
  if (!context) throw new Error('useEventContext must be used inside an EventProvider');
  return context;
}
